import type { ComponentChildren } from 'preact';

export function Meter({
  value,
  max,
  tone = 'progress',
  label,
}: {
  value: number;
  max: number;
  tone?: 'progress' | 'energy' | 'wellbeing' | 'wear';
  label?: string;
}) {
  const percent = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      class={`meter meter-${tone}`}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.round(max)}
      aria-valuenow={Math.round(value)}
    >
      <div class="meter-fill" style={{ width: `${percent}%` }} />
    </div>
  );
}

export function Card({ title, children, className }: { title?: string; children: ComponentChildren; className?: string }) {
  return (
    <section class={`card ${className ?? ''}`}>
      {title && <h2 class="card-title">{title}</h2>}
      {children}
    </section>
  );
}

export function Line({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' | 'strong' }) {
  return (
    <div class="line">
      <span class="line-label">{label}</span>
      <span class={`line-value ${tone ? `tone-${tone}` : ''}`}>{value}</span>
    </div>
  );
}

export function Modal({ title, eyebrow, children }: { title: string; eyebrow?: string; children: ComponentChildren }) {
  return (
    <div class="modal-backdrop">
      <div class="modal" role="dialog" aria-modal="true" aria-label={title}>
        {eyebrow && <p class="eyebrow">{eyebrow}</p>}
        <h2 class="modal-title">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/**
 * Une courbe simple : verte si elle finit plus haut qu'elle n'a commencé, rouge sinon.
 * Une courbe de référence (ce qui a été versé, par exemple) peut s'y ajouter en pointillés.
 * Avec une référence, la couleur compare la fin des deux courbes : au-dessus, c'est un gain.
 */
export function LineChart({ values, reference, label }: { values: number[]; reference?: number[]; label: string }) {
  const width = 300;
  const height = 100;
  const all = reference ? [...values, ...reference] : values;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;
  const toPoints = (series: number[]) => {
    const step = series.length > 1 ? width / (series.length - 1) : 0;
    return series
      .map((value, index) => `${(index * step).toFixed(1)},${(height - ((value - min) / span) * height).toFixed(1)}`)
      .join(' ');
  };
  const last = values[values.length - 1];
  const up = reference ? last >= reference[reference.length - 1] : last >= values[0];
  return (
    <svg
      class={`chart ${up ? 'chart-up' : 'chart-down'}`}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      {reference && <polyline class="chart-reference" points={toPoints(reference)} vector-effect="non-scaling-stroke" />}
      <polyline points={toPoints(values)} vector-effect="non-scaling-stroke" />
    </svg>
  );
}
